import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { getSignedUrl } from 'store/commonSlice';

const StyledModal = styled(Modal)`
	z-index: 2000 !important;

	.ant-modal-content {
		padding: 0;
		border-radius: 8px;
		overflow: hidden;
	}

	.ant-modal-header {
		background: #18405d;
		border-bottom: none;
		padding: 16px 24px;
		margin: 0;
	}

	.ant-modal-title {
		color: #fff;
		font-size: 18px;
		font-weight: 600;
	}

	.ant-modal-close {
		color: #fff;
		top: 16px;
		right: 24px;

		&:hover {
			color: #fff;
		}
	}

	.ant-modal-body {
		padding: 24px;
		background: #f5f5f5;
		min-height: 400px;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.ant-modal-mask {
		z-index: 1999 !important;
	}
`;

const PreviewContainer = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #fff;
	border-radius: 4px;
	overflow: hidden;
`;

const ImagePreview = styled.img`
	max-width: 100%;
	max-height: 70vh;
	object-fit: contain;
`;

const PDFPreview = styled.iframe`
	width: 100%;
	height: 70vh;
	border: none;
`;

const LoadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
`;

const ErrorContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 16px;
	color: #ff4d4f;
`;

/**
 * Universal File Preview Modal Component
 * Displays uploaded files (PDFs, images) in a modal
 *
 * @param {boolean} visible - Whether the modal is visible
 * @param {string} fileKey - The file key from S3/storage
 * @param {string} fileName - Optional file name to display
 * @param {string} fileType - Optional file type (pdf, image, etc.)
 * @param {string} entity - Entity type for file lookup (default: 'vendor')
 * @param {Function} onClose - Callback when modal is closed
 */
export default function FilePreviewModal({
	visible,
	fileKey,
	fileName = '',
	fileType = null,
	entity = 'vendor',
	onClose,
}) {
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [fileUrl, setFileUrl] = useState(null);
	const [detectedType, setDetectedType] = useState(null);

	useEffect(() => {
		if (!visible || !fileKey) {
			setFileUrl(null);
			setError(null);
			setLoading(false);
			return;
		}

		const fetchFileUrl = async () => {
			setLoading(true);
			setError(null);

			try {
				// Get signed URL for viewing the file
				const response = await dispatch(
					getSignedUrl({
						url_type: 'view',
						data: {
							entity,
							keys: [fileKey],
						},
					})
				);

				console.log('FilePreviewModal - API Response:', response);

				// Handle response structure: response.data is an array
				if (response?.data && Array.isArray(response.data) && response.data.length > 0) {
					const fileData = response.data[0];
					if (fileData?.url) {
						setFileUrl(fileData.url);

						// Detect file type from extension or content
						const detected = detectFileType(fileKey, fileName, fileType);
						setDetectedType(detected);
					} else {
						throw new Error('URL not found in response');
					}
				} else {
					throw new Error('Invalid response structure or empty data array');
				}
			} catch (err) {
				console.error('Error fetching file URL:', err);
				console.error('FileKey:', fileKey);
				console.error('Entity:', entity);
				setError(err.message || 'Failed to load file. Please try again.');
				message.error('Failed to load file');
			} finally {
				setLoading(false);
			}
		};

		fetchFileUrl();
	}, [visible, fileKey, fileName, fileType, entity, dispatch]);

	/**
	 * Detect file type from file key, name, or provided type
	 */
	const detectFileType = (key, name, providedType) => {
		if (providedType) {
			return providedType.toLowerCase();
		}

		const fileToCheck = name || key || '';
		const extension = fileToCheck.split('.').pop()?.toLowerCase();

		if (['pdf'].includes(extension)) {
			return 'pdf';
		}

		if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) {
			return 'image';
		}

		// Default to PDF if uncertain
		return 'pdf';
	};

	const handleClose = () => {
		setFileUrl(null);
		setError(null);
		setLoading(false);
		if (onClose) {
			onClose();
		}
	};

	const renderPreview = () => {
		if (loading) {
			return (
				<LoadingContainer>
					<Spin size="large" />
					<div>Loading file...</div>
				</LoadingContainer>
			);
		}

		if (error) {
			return (
				<ErrorContainer>
					<div style={{ fontSize: '16px', fontWeight: 500 }}>Error</div>
					<div>{error}</div>
				</ErrorContainer>
			);
		}

		if (!fileUrl) {
			return (
				<ErrorContainer>
					<div>No file to display</div>
				</ErrorContainer>
			);
		}

		if (detectedType === 'pdf') {
			return (
				<PDFPreview
					src={`${fileUrl}#toolbar=1&navpanes=0&scrollbar=1`}
					title={fileName || 'PDF Preview'}
					onLoad={() => setLoading(false)}
					onError={() => {
						setError('Failed to load PDF. Please try opening in a new tab.');
						setLoading(false);
					}}
				/>
			);
		}

		if (detectedType === 'image') {
			return (
				<ImagePreview
					src={fileUrl}
					alt={fileName || 'Image Preview'}
					onLoad={() => setLoading(false)}
					onError={(e) => {
						console.error('Image load error:', e);
						setError(
							'Failed to load image. The file may not exist or the URL may have expired.'
						);
						setLoading(false);
					}}
				/>
			);
		}

		// Fallback: try to open in new tab
		return (
			<PreviewContainer>
				<div style={{ textAlign: 'center', padding: '20px' }}>
					<div style={{ marginBottom: '16px', fontSize: '16px' }}>
						File preview not available for this file type
					</div>
					<a
						href={fileUrl}
						target="_blank"
						rel="noopener noreferrer"
						style={{
							display: 'inline-block',
							padding: '8px 16px',
							backgroundColor: '#1890ff',
							color: 'white',
							borderRadius: '4px',
							textDecoration: 'none',
						}}
					>
						Open file in new tab
					</a>
				</div>
			</PreviewContainer>
		);
	};

	return (
		<StyledModal
			title={fileName || 'File Preview'}
			open={visible}
			onCancel={handleClose}
			footer={null}
			width={900}
			closeIcon={<CloseOutlined style={{ color: '#fff' }} />}
			centered
			zIndex={2000}
		>
			<PreviewContainer>{renderPreview()}</PreviewContainer>
		</StyledModal>
	);
}

FilePreviewModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	fileKey: PropTypes.string,
	fileName: PropTypes.string,
	fileType: PropTypes.string,
	entity: PropTypes.string,
	onClose: PropTypes.func.isRequired,
};
